/* ------------------------------------------------------------------
   TextPanel – mobile-friendly
   • Desktop  : same 190-px side fly-out
   • Mobile   : full-width card with compressed layout +
                horizontal font scroller + collapsible “Background”
------------------------------------------------------------------- */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Collapse } from 'react-bootstrap';
import useIsMobile from '../../../../../hooks/useIsMobile';

const FONTS = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Tahoma',
  'Verdana', 'Trebuchet MS', 'Courier New', 'Lucida Console', 'Lucida Sans',
  'Palatino Linotype', 'Garamond', 'Segoe UI', 'Roboto', 'Open Sans',
  'Montserrat', 'Lato', 'Poppins', 'Oswald', 'Raleway',
];

export default function TextPanel ({
  fontSize, setFontSize,
  fontFamily, setFontFamily,
  color, setColor,
  bgColor, setBgColor,
  onAdd,
}) {
  const isMobile = useIsMobile();
  const [showBg, setShowBg] = useState(false);              // accordion
  const isNone = bgColor === 'transparent';

  /* swatch styles (unchanged) */
  const swatchBase = {
    width: 44, height: 32,
    border: '1px solid #ced4da', borderRadius: 6,
  };
  const swatchStyle = isNone
    ? { ...swatchBase, background:'#fff',
        backgroundImage:'linear-gradient(45deg,transparent 38%,#ff0000 38%,#ff0000 62%,transparent 62%)' }
    : { ...swatchBase, background:bgColor };

  /* ===========  MOBILE  =========== */
  if (isMobile) {
    return (
      <div className="p-2 d-flex flex-column" style={{ gap:12,maxHeight:'60vh',overflowY:'auto' }}>
        {/* sticky add-text */}
        <div style={{ position:'sticky', top:0, background:'#fff', zIndex:3 }}>
          <Button className="w-100" size="sm" onClick={onAdd}>+ Add Text</Button>
        </div>

        {/* font + size on one row */}
        <div className="d-flex align-items-center small" style={{ gap:8 }}>
          <div className="flex-grow-1">
            <Form.Label className="mb-1">Font</Form.Label>
            <div className="d-flex overflow-auto" style={{ gap:6 }}>
              {FONTS.map(f => (
                <Button
                  key={f}
                  size="sm"
                  variant={fontFamily===f ? 'primary' : 'light'}
                  style={{ minWidth:70, fontFamily:f }}
                  onClick={()=>setFontFamily(f)}
                >
                  {f.split(' ')[0]}
                </Button>
              ))}
            </div>
          </div>

          <div style={{ width: 72 }}>
            <Form.Label className="mb-1">Size</Form.Label>
            <Form.Range
              min={16} max={96}
              value={fontSize}
              onChange={e=>setFontSize(+e.target.value)}
            />
          </div>
        </div>

        {/* text colour */}
        <div className="small">
          <Form.Label className="mb-1">Text Colour</Form.Label>
          <Form.Control type="color" value={color} onChange={e=>setColor(e.target.value)} />
        </div>

        {/* background (collapsible) */}
        <Button
          variant="link" size="sm" className="p-0 text-start"
          onClick={()=>setShowBg(v=>!v)}
        >
          {showBg ? 'Hide background ⌃' : 'Show background ⌄'}
        </Button>
        <Collapse in={showBg}>
          <div className="small">
            <Form.Label className="mb-1">Background</Form.Label>
            <div style={{ position:'relative', ...swatchStyle, cursor:'pointer' }}>
              <input
                type="color"
                value={isNone ? '#000000' : bgColor}
                onChange={e=>setBgColor(e.target.value)}
                style={{
                  position:'absolute', top:0,left:0,
                  width:'100%',height:'100%',opacity:0,cursor:'pointer',
                }}
              />
            </div>
            {!isNone && (
              <Button variant="link" size="sm" className="p-0 mt-1"
                onClick={()=>setBgColor('transparent')}
              >Clear background
              </Button>
            )}
          </div>
        </Collapse>
      </div>
    );
  }

  /* ===========  DESKTOP / TABLET  (unchanged) =========== */
  return (
    <div
      className="position-absolute d-flex flex-column"
      style={{ left:0, top:0, bottom:0, width:190,
               background:'#fff', zIndex:2, overflowY:'auto',
               padding:12, gap:14 }}
    >
      <Button variant="primary" size="sm" onClick={onAdd}>+ Add Text</Button>

      {/* font */}
      <div className="small">
        <Form.Label className="mb-1">Font</Form.Label>
        <Form.Select size="sm" value={fontFamily} onChange={e=>setFontFamily(e.target.value)}>
          {FONTS.map(f=>(
            <option key={f} value={f} style={{ fontFamily:f }}>{f}</option>
          ))}
        </Form.Select>
      </div>

      {/* size */}
      <div className="small">
        <Form.Label className="mb-1">Font Size</Form.Label>
        <Form.Range min={16} max={96} value={fontSize} onChange={e=>setFontSize(+e.target.value)} />
      </div>

      {/* colour */}
      <div className="small">
        <Form.Label className="mb-1">Text Colour</Form.Label>
        <Form.Control type="color" value={color} onChange={e=>setColor(e.target.value)} />
      </div>

      {/* background */}
      <div className="small">
        <Form.Label className="mb-1">Background</Form.Label>
        <div style={{ position:'relative', ...swatchStyle, cursor:'pointer' }}>
          <input
            type="color"
            value={isNone ? '#000000' : bgColor}
            onChange={e=>setBgColor(e.target.value)}
            style={{
              position:'absolute', top:0,left:0,
              width:'100%',height:'100%',opacity:0,cursor:'pointer',
            }}
          />
        </div>
        {!isNone && (
          <Button variant="link" size="sm" className="p-0 mt-1"
            onClick={()=>setBgColor('transparent')}
          >Clear background
          </Button>
        )}
      </div>
    </div>
  );
}

TextPanel.propTypes = {
  fontSize      : PropTypes.number,
  setFontSize   : PropTypes.func,
  fontFamily    : PropTypes.string,
  setFontFamily : PropTypes.func,
  color         : PropTypes.string,
  setColor      : PropTypes.func,
  bgColor       : PropTypes.string,
  setBgColor    : PropTypes.func,
  onAdd         : PropTypes.func,
};
